import {
  HeaderSection,
  generateFormRules,
  getStandardError,
} from '@qlibs/react-components';
import {
  Card,
  Divider,
  Form,
  Grid,
  Input,
  Row,
  Select,
  Switch,
  message,
} from 'antd';
import React, { useEffect } from 'react';
import SectionContent from '../../components/SectionContent';
import { useNavigate } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import {
  CustomFormItemPer1,
  CustomFormItemPer2,
  CustomFormItemPer3,
} from '../user/components/CustomFormItem';
import useAddress from '../../hooks/useAddress';
import { httpRequest } from '../../helpers/api';
import { ICreateGroup } from './types/group.type';
import {
  GroupMobileActionButton,
  GroupMobileField,
  GroupMobileForm,
  GroupMobileHeaderActions,
  GroupMobilePage,
  GroupMobileSecondaryButton,
  GroupMobileSection,
  GroupMobileSectionHeader,
  GroupMobileSwitchField,
  MobileGroupHeader,
} from './mobile';

const GroupCreate = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [isLoadingAction, setIsLoadingAction] = React.useState(false);
  const [form] = Form.useForm();
  const {
    fetchRegion,
    provinces,
    districts,
    cities,
    activeProvince,
    activeCity,
    searchAddress,
    setActiveCity,
    setActiveProvince,
  } = useAddress();

  async function createGroup(values: ICreateGroup) {
    try {
      setIsLoadingAction(true);

      const formData = {
        name: values.name,
        nickname: values.nickname,
        phoneNumber: values.phoneNumber,
        address: {
          address: values.address,
          provinceId: values.province,
          cityId: values.city,
          districtId: values.district,
        },
        fax: values.fax,
        status: values.status ? 'active' : 'inactive',
      };

      await httpRequest.post('/group', formData);

      message.success(`Success add ${values.name}`);
      form.resetFields();
      backToList(navigate, '/group');
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  const handleSubmit = async (values: any) => {
    createGroup(values);
  };

  useEffect(() => {
    fetchRegion('provinces');
  }, []);

  useEffect(() => {
    if (form.getFieldValue('province')) fetchRegion('cities');
    if (form.getFieldValue('city')) fetchRegion('districts');
  }, [activeProvince, activeCity]);

  const renderMobileContent = () => (
    <GroupMobilePage>
      <MobileGroupHeader
        title="Add Group"
        onBack={() => backToList(navigate, '/group')}
        action={
          <GroupMobileHeaderActions>
            <GroupMobileSecondaryButton onClick={() => backToList(navigate, '/group')}>
              Cancel
            </GroupMobileSecondaryButton>
            <GroupMobileActionButton
              type="primary"
              loading={isLoadingAction}
              onClick={() => form.submit()}
            >
              Save
            </GroupMobileActionButton>
          </GroupMobileHeaderActions>
        }
      />

      <GroupMobileForm>
        <Form
          form={form}
          name="profileForm"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <GroupMobileSection>
            <GroupMobileSectionHeader>
              <h2>Information</h2>
              <p>General information about this group</p>
            </GroupMobileSectionHeader>
            <GroupMobileField
              label="Group Name"
              name="name"
              rules={generateFormRules('Group Name', ['required'])}
            >
              <Input placeholder="Input group name" />
            </GroupMobileField>
            <GroupMobileField
              label="Group Nickname"
              name="nickname"
              rules={generateFormRules('Group Nickname', ['required'])}
            >
              <Input placeholder="Input group nickname" />
            </GroupMobileField>
            <GroupMobileField
              label="Phone Number"
              name="phoneNumber"
              rules={generateFormRules('Group Phone Number', ['phoneNumber'])}
            >
              <Input placeholder="Input group phone number contact" />
            </GroupMobileField>
            <GroupMobileField
              label="Fax"
              name="fax"
              rules={generateFormRules('Fax', ['numeric'])}
            >
              <Input placeholder="Input group fax contact" />
            </GroupMobileField>
          </GroupMobileSection>

          <GroupMobileSection>
            <GroupMobileSectionHeader>
              <h2>Address</h2>
              <p>Information about address of this group</p>
            </GroupMobileSectionHeader>
            <GroupMobileField
              label="Address"
              name="address"
              rules={generateFormRules('Address', ['required'])}
            >
              <Input placeholder="Input address" />
            </GroupMobileField>
            <GroupMobileField
              label="Province"
              name="province"
              rules={generateFormRules('Province', ['required'])}
            >
              <Select
                showSearch
                placeholder="Search province"
                style={{ width: '100%' }}
                onSearch={(value) => searchAddress('provinces', value)}
                onChange={(value) => {
                  setActiveProvince(value);
                  form.setFieldsValue({
                    city: '',
                    district: '',
                  });
                }}
                allowClear
                onClear={() => searchAddress('provinces', '')}
                filterOption={false}
                options={provinces.map((item) => ({
                  value: item.provinceId,
                  label: item.provinceName,
                }))}
              />
            </GroupMobileField>
            <GroupMobileField
              label="City"
              name="city"
              rules={generateFormRules('City', ['required'])}
            >
              <Select
                showSearch
                disabled={form.getFieldValue('province') ? false : true}
                placeholder="Search cities"
                style={{ width: '100%' }}
                onSearch={(value) => searchAddress('cities', value)}
                onChange={(value) => {
                  setActiveCity(value);
                  form.setFieldsValue({
                    district: '',
                  });
                }}
                allowClear
                onClear={() => searchAddress('cities', '')}
                filterOption={false}
                options={cities.map((item) => ({
                  value: item.cityId,
                  label: item.cityName,
                }))}
              />
            </GroupMobileField>
            <GroupMobileField
              label="District"
              name="district"
              rules={generateFormRules('District', ['required'])}
            >
              <Select
                showSearch
                disabled={form.getFieldValue('city') ? false : true}
                placeholder="Search district"
                style={{ width: '100%' }}
                onSearch={(value) => searchAddress('districts', value)}
                onChange={(value) => setActiveCity(value)}
                allowClear
                onClear={() => searchAddress('districts', '')}
                filterOption={false}
                options={districts.map((item) => ({
                  value: item.districtId,
                  label: item.districtName,
                }))}
              />
            </GroupMobileField>
          </GroupMobileSection>

          <GroupMobileSection>
            <GroupMobileSectionHeader>
              <h2>Setting</h2>
              <p>Setting about this group</p>
            </GroupMobileSectionHeader>
            <GroupMobileSwitchField
              label="Status"
              name="status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch defaultChecked className="custom-switch" />
            </GroupMobileSwitchField>
          </GroupMobileSection>
        </Form>
      </GroupMobileForm>
    </GroupMobilePage>
  );

  if (isMobile) {
    return renderMobileContent();
  }

  return (
    <React.Fragment>
      <HeaderSection
        icon="back"
        title={'Add Group'}
        subtitle={'Create new group'}
        showCancelButton
        showSaveButton
        isLoading={isLoadingAction}
        onSave={() => form.submit()}
      />

      <Card>
        <Form
          form={form}
          name="profileForm"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <SectionContent
            groupTitle="Information"
            subTitle="General infromation about this group"
          >
            <CustomFormItemPer1
              label="Group Name"
              name="name"
              rules={generateFormRules('Group Name', ['required'])}
            >
              <Input placeholder="Input group name" />
            </CustomFormItemPer1>
            <Row>
              <CustomFormItemPer3
                label="Group Nickname"
                name="nickname"
                rules={generateFormRules('Group Nickname', ['required'])}
              >
                <Input placeholder="Input group nickname" />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label="Phone Number"
                name="phoneNumber"
                rules={generateFormRules('Group Phone Number', ['phoneNumber'])}
              >
                <Input placeholder="Input group phone number contact" />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label="Fax"
                name="fax"
                rules={generateFormRules('Fax', ['numeric'])}
              >
                <Input placeholder="Input group fax contact" />
              </CustomFormItemPer3>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle="Address"
            subTitle="Information about address of this group"
          >
            <CustomFormItemPer1
              label="Address"
              name="address"
              rules={generateFormRules('Address', ['required'])}
            >
              <Input placeholder="Input address" />
            </CustomFormItemPer1>
            <Row>
              <CustomFormItemPer3
                label="Province"
                name="province"
                rules={generateFormRules('Province', ['required'])}
              >
                <Select
                  showSearch
                  placeholder="Search province"
                  style={{ width: '100%' }}
                  onSearch={(value) => searchAddress('provinces', value)}
                  onChange={(value) => {
                    setActiveProvince(value);
                    form.setFieldsValue({
                      city: '',
                      district: '',
                    });
                  }}
                  allowClear
                  onClear={() => searchAddress('provinces', '')}
                  filterOption={false}
                  options={provinces.map((item) => ({
                    value: item.provinceId,
                    label: item.provinceName,
                  }))}
                />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label="City"
                name="city"
                rules={generateFormRules('City', ['required'])}
              >
                <Select
                  showSearch
                  disabled={form.getFieldValue('province') ? false : true}
                  placeholder="Search cities"
                  style={{ width: '100%' }}
                  onSearch={(value) => searchAddress('cities', value)}
                  onChange={(value) => {
                    setActiveCity(value);
                    form.setFieldsValue({
                      district: '',
                    });
                  }}
                  allowClear
                  onClear={() => searchAddress('cities', '')}
                  filterOption={false}
                  options={cities.map((item) => ({
                    value: item.cityId,
                    label: item.cityName,
                  }))}
                />
              </CustomFormItemPer3>
              <CustomFormItemPer3
                label="District"
                name="district"
                rules={generateFormRules('District', ['required'])}
              >
                <Select
                  showSearch
                  disabled={form.getFieldValue('city') ? false : true}
                  placeholder="Search district"
                  style={{ width: '100%' }}
                  onSearch={(value) => searchAddress('districts', value)}
                  onChange={(value) => setActiveCity(value)}
                  allowClear
                  onClear={() => searchAddress('districts', '')}
                  filterOption={false}
                  options={districts.map((item) => ({
                    value: item.districtId,
                    label: item.districtName,
                  }))}
                />
              </CustomFormItemPer3>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Setting`}
            subTitle="Setting about this group"
          >
            <CustomFormItemPer1
              label="Status"
              name="status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch defaultChecked className="custom-switch" />
            </CustomFormItemPer1>
          </SectionContent>
        </Form>
      </Card>
    </React.Fragment>
  );
};

export default GroupCreate;
